import React from 'react';
import { Card } from '@/components/ui/card';

// Base Skeleton component with pulse animation
const Skeleton = ({ className = '' }) => (
  <div className={`animate-pulse bg-slate-200 rounded ${className}`} />
);

// Skeleton for listing cards
export const ListingCardSkeleton = () => (
  <Card className="p-6 h-full">
    <div className="flex items-start justify-between mb-4">
      <div className="flex items-center space-x-3">
        <Skeleton className="w-10 h-10 rounded-xl" />
        <Skeleton className="w-24 h-3" />
      </div>
      <Skeleton className="w-16 h-6 rounded-full" />
    </div>
    <Skeleton className="w-3/4 h-5 mb-2" />
    <Skeleton className="w-full h-3 mb-1" />
    <Skeleton className="w-2/3 h-3 mb-4" />
    <div className="flex gap-2 mb-4">
      <Skeleton className="w-20 h-6 rounded-full" />
      <Skeleton className="w-16 h-6 rounded-full" />
    </div>
    <div className="flex items-center justify-between pt-4 border-t border-slate-100">
      <div className="flex items-center space-x-4">
        <Skeleton className="w-20 h-3" />
        <Skeleton className="w-16 h-3" />
      </div>
      <Skeleton className="w-5 h-5 rounded" />
    </div>
  </Card>
);

// Skeleton for professional cards
export const ProfessionalCardSkeleton = () => (
  <Card className="p-6">
    <div className="flex items-start gap-4">
      <Skeleton className="w-16 h-16 rounded-xl flex-shrink-0" />
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-2">
          <Skeleton className="w-32 h-5" />
          <Skeleton className="w-12 h-5 rounded-full" />
        </div>
        <Skeleton className="w-24 h-4 mb-2" />
        <Skeleton className="w-full h-3 mb-1" />
        <Skeleton className="w-3/4 h-3 mb-3" />
        <div className="flex gap-2">
          <Skeleton className="w-16 h-6 rounded-full" />
          <Skeleton className="w-20 h-6 rounded-full" />
          <Skeleton className="w-14 h-6 rounded-full" />
        </div>
      </div>
    </div>
    <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
      <div className="flex items-center gap-4">
        <Skeleton className="w-16 h-4" />
        <Skeleton className="w-20 h-4" />
      </div>
      <Skeleton className="w-24 h-9 rounded-full" />
    </div>
  </Card>
);

// Skeleton for message/conversation items
export const MessageSkeleton = () => (
  <div className="p-4 border-b border-slate-100">
    <div className="flex items-center gap-3">
      <Skeleton className="w-12 h-12 rounded-full" />
      <div className="flex-1">
        <Skeleton className="w-32 h-4 mb-2" />
        <Skeleton className="w-48 h-3" />
      </div>
      <Skeleton className="w-12 h-3" />
    </div>
  </div>
);

// Skeleton for stats cards
export const StatsCardSkeleton = () => (
  <Card className="p-6">
    <div className="flex items-center justify-between mb-4">
      <Skeleton className="w-10 h-10 rounded-xl" />
      <Skeleton className="w-16 h-6 rounded-full" />
    </div>
    <Skeleton className="w-20 h-8 mb-2" />
    <Skeleton className="w-24 h-3" />
  </Card>
);

// Skeleton for profile header
export const ProfileHeaderSkeleton = () => (
  <div className="flex items-start gap-6 mb-8">
    <Skeleton className="w-24 h-24 rounded-2xl" />
    <div className="flex-1">
      <div className="flex items-center gap-3 mb-2">
        <Skeleton className="w-48 h-7" />
        <Skeleton className="w-20 h-6 rounded-full" />
      </div>
      <Skeleton className="w-32 h-4 mb-3" />
      <Skeleton className="w-full h-3 mb-1" />
      <Skeleton className="w-3/4 h-3 mb-4" />
      <div className="flex gap-3">
        <Skeleton className="w-28 h-10 rounded-full" />
        <Skeleton className="w-28 h-10 rounded-full" />
      </div>
    </div>
  </div>
);

// Skeleton for table rows
export const TableRowSkeleton = ({ columns = 5 }) => (
  <tr className="border-b border-slate-100">
    {Array.from({ length: columns }).map((_, i) => (
      <td key={i} className="p-4">
        <Skeleton className="w-full h-4" />
      </td>
    ))}
  </tr>
);

// Skeleton for dashboard overview
export const DashboardSkeleton = () => (
  <div className="space-y-6">
    {/* Stats Grid */}
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {Array.from({ length: 4 }).map((_, i) => (
        <StatsCardSkeleton key={i} />
      ))}
    </div>
    {/* Content Area */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card className="p-6">
        <Skeleton className="w-32 h-5 mb-4" />
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3">
              <Skeleton className="w-10 h-10 rounded-full" />
              <div className="flex-1">
                <Skeleton className="w-24 h-4 mb-1" />
                <Skeleton className="w-32 h-3" />
              </div>
            </div>
          ))}
        </div>
      </Card>
      <Card className="p-6">
        <Skeleton className="w-32 h-5 mb-4" />
        <Skeleton className="w-full h-48 rounded-xl" />
      </Card>
    </div>
  </div>
);

// Grid of listing skeletons
export const ListingsGridSkeleton = ({ count = 6 }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
    {Array.from({ length: count }).map((_, i) => (
      <ListingCardSkeleton key={i} />
    ))}
  </div>
);

// Grid of professional skeletons
export const ProfessionalsGridSkeleton = ({ count = 6 }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
    {Array.from({ length: count }).map((_, i) => (
      <ProfessionalCardSkeleton key={i} />
    ))}
  </div>
);

// List of message skeletons
export const MessagesListSkeleton = ({ count = 5 }) => (
  <div>
    {Array.from({ length: count }).map((_, i) => (
      <MessageSkeleton key={i} />
    ))}
  </div>
);

export default Skeleton;
